import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2, Plane, FileText, Download, ArrowRight } from "lucide-react";
import { PACKAGES } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 15;
const CALENDLY_URL = "https://calendly.com/hello-theditchcollective/30min";

function CalendlyEmbed() {
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);
    return () => document.body.removeChild(script);
  }, []);

  return (
    <div
      className="calendly-inline-widget rounded-2xl overflow-hidden border border-[var(--line)]"
      data-url={`${CALENDLY_URL}?hide_gdpr_banner=1&primary_color=0D2F4D`}
      style={{ minWidth: "320px", height: "640px" }}
      data-testid="package-confirmation-calendly"
    />
  );
}

export default function PackageConfirmation() {
  const [phase, setPhase] = useState("checking"); // checking | ready | error
  const [info, setInfo] = useState(null); // { packageTitle, firstName }
  const [errorMsg, setErrorMsg] = useState("");
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");

    const fail = (msg) => {
      setErrorMsg(msg);
      setPhase("error");
    };

    const pollStatus = async () => {
      if (polls.current >= MAX_POLLS) {
        fail("We couldn't confirm your payment in time. If you were charged, your confirmation and agreement are on their way by email.");
        return;
      }
      polls.current += 1;
      try {
        const { data } = await axios.get(`${API}/packages/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setInfo({
            packageTitle: data.package_title,
            packageId: data.package_id,
            firstName: data.first_name,
            agreementUrl: data.agreement_url,
            intakeUrl: data.intake_url,
          });
          setPhase("ready");
        } else if (data.status === "expired") {
          fail("Your checkout session expired before payment was completed. No charge was made — head back and try again.");
        } else {
          setTimeout(pollStatus, 2000);
        }
      } catch {
        fail("Something went wrong while confirming your payment.");
      }
    };

    if (sessionId) pollStatus();
    else fail("This page needs a valid checkout session.");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only, reads session_id from URL
  }, []);

  return (
    <div className="min-h-screen bg-[var(--cream)] flex flex-col" data-testid={PACKAGES.confirmationPage}>
      <header className="px-5 md:px-8 py-5">
        <a href="/" className="inline-flex items-center gap-2 font-display font-extrabold text-[var(--ink)]">
          <Plane size={18} /> The Ditch Collective
        </a>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 pb-20">
        <div className={`w-full bg-[var(--paper)] rounded-3xl border border-[var(--line)] p-8 md:p-10 text-center shadow-xl ${phase === "ready" ? "max-w-2xl" : "max-w-lg"}`}>
          {phase === "checking" && (
            <>
              <Loader2 size={40} className="mx-auto animate-spin text-[var(--ink)]" />
              <h1 className="font-display text-2xl font-extrabold mt-6">Confirming your payment…</h1>
              <p className="text-[var(--ocean)] mt-2 text-sm">Hang tight — this only takes a few seconds.</p>
            </>
          )}

          {phase === "ready" && (
            <>
              <CheckCircle2 size={44} className="mx-auto text-[var(--lime-deep)]" />
              <span className="num-tag text-xs block mt-6">{info.packageTitle}</span>
              <h1 className="font-display text-3xl font-extrabold mt-2 leading-tight">
                You're in{info.firstName ? `, ${info.firstName}` : ""}.
              </h1>

              {info.packageId === "resume_review" ? (
                <>
                  <p className="text-[var(--ocean)] mt-3 text-sm leading-relaxed">
                    Next up: tell us about the role you're targeting and upload your current CV.
                    Our team will polish the AI-drafted version and email your Australian-ready CV
                    within <b>48 hours</b>.
                  </p>
                  {info.intakeUrl ? (
                    <a
                      href={info.intakeUrl}
                      data-testid="resume-review-open-builder"
                      className="mt-6 inline-flex items-center justify-center gap-2 py-4 px-8 rounded-full bg-[var(--ink)] text-[var(--paper)] font-display font-bold hover:brightness-110 transition-all"
                    >
                      Open the CV Builder <ArrowRight size={16} />
                    </a>
                  ) : (
                    <div className="mt-6 text-left bg-[var(--mist-soft)] rounded-2xl p-5 text-sm text-[var(--ink)] leading-relaxed" data-testid="resume-review-email-fallback">
                      We're preparing your CV Builder link — it will land in your inbox within a
                      couple of minutes at the email you used to check out. If it hasn't arrived
                      after 10 minutes, email{" "}
                      <a href="mailto:hello@theditchcollective.co.nz" className="underline">
                        hello@theditchcollective.co.nz
                      </a>{" "}
                      and we'll send it manually.
                    </div>
                  )}
                  <p className="text-xs text-[var(--ocean)] mt-4">
                    We've also emailed you this link so you can come back to it any time.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-[var(--ocean)] mt-3 text-sm leading-relaxed">
                    The team will be in touch within 48 hours to kick off your move.
                  </p>
                  <div className="mt-6 flex items-start gap-3 text-left bg-[var(--mist-soft)] rounded-2xl p-5">
                    <FileText size={18} className="shrink-0 mt-0.5 text-[var(--ink)]" />
                    <div>
                      <p className="text-sm text-[var(--ink)] leading-relaxed">
                        Your <span className="font-bold">Client Service Agreement</span> is ready, pre-filled
                        with your details. Have a read, sign it, and send it back to{" "}
                        <a href="mailto:hello@theditchcollective.co.nz" className="underline">
                          hello@theditchcollective.co.nz
                        </a>
                        . You'll also get the link by email.
                      </p>
                      {info.agreementUrl && (
                        <a
                          data-testid="package-agreement-download"
                          href={info.agreementUrl}
                          className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-[var(--ink)] underline underline-offset-4 hover:text-[var(--ink-soft)]"
                        >
                          <Download size={14} /> Download your agreement (PDF)
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="mt-8 text-left">
                    <span className="num-tag text-xs">One more thing</span>
                    <h2 className="font-display text-xl font-extrabold mt-1">
                      Lock in your kickoff call with the team
                    </h2>
                    <p className="text-[var(--ocean)] text-sm mt-1 mb-4">
                      Pick a time that suits — beat the 48 hours and get moving today.
                    </p>
                    <CalendlyEmbed />
                  </div>
                </>
              )}

              <a href="/" className="mt-7 inline-block btn-ghost text-sm">
                ← Back to the site
              </a>
            </>
          )}

          {phase === "error" && (
            <>
              <AlertCircle size={44} className="mx-auto text-[var(--ink)]" />
              <h1 className="font-display text-2xl font-extrabold mt-6" data-testid={PACKAGES.error}>
                Hmm, that didn't work
              </h1>
              <p className="text-[var(--ocean)] mt-3 text-sm leading-relaxed">{errorMsg}</p>
              <p className="text-sm text-[var(--ink)] mt-5">
                Flick us an email at{" "}
                <a href="mailto:hello@theditchcollective.co.nz" className="font-bold underline">
                  hello@theditchcollective.co.nz
                </a>{" "}
                and we'll sort you out.
              </p>
              <a href="/" className="mt-6 inline-block btn-ghost text-sm">
                ← Back to the site
              </a>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
